import {
  type CommissionResponseExtraFragment,
  CommissionStatus,
  useGetOrderDetailsQuery,
} from '@/affiliate-dashboard/graphql';
import { Drawer } from '@/components/base/drawer';
import { PageHeading } from '@/components/base/page-heading';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';

export function OrderDetailDrawer({
  orderId,
  isOpen,
  onClose,
  commissionData,
}: {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
  commissionData: CommissionResponseExtraFragment | null;
}): JSX.Element {
  const { data, loading } = useGetOrderDetailsQuery({ variables: { orderId } });
  const orderDetails = data?.orderDetails;
  const intl = useIntl();
  const { t } = useTranslation();

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Drawer
      triggerClassname="cursor-pointer w-full"
      contentClassName="h-[50%]"
      isOpen={isOpen}
      trigger={<></>}
      handleChange={() => {
        onClose();
      }}
    >
      <div>
        {commissionData?.status === CommissionStatus.Locked && (
          <span className="block w-full text-center text-warning">
            <Trans
              components={{
                1: (
                  <Link
                    className="underline underline-offset-2"
                    href="/dashboard/upgrade"
                  />
                ),
              }}
              i18nKey="commissions.detail"
            />
          </span>
        )}
        <PageHeading>{t('orderDetail.title')}</PageHeading>
        <div className="flex w-full flex-col divide-y divide-gray-700 [&>*]:py-2">
          <div className="flex w-full justify-between">
            <span className="font-bold">{t('orderDetail.orderId')}</span>{' '}
            <span className="text-end">{orderDetails?.id}</span>
          </div>
          <div className="flex w-full justify-between">
            <span className="font-bold">{t('orderDetail.amount')}</span>
            <span>
              {intl.formatNumber(orderDetails?.amount ?? 0, {
                style: 'currency',
                currency: orderDetails?.currency ?? 'USD',
              })}
            </span>
          </div>
          <div className="flex w-full justify-between">
            <span className="font-bold">{t('orderDetail.orderNumber')}</span>
            <span>{orderDetails?.orderNumber}</span>
          </div>
          {orderDetails?.createdAt && (
            <div className="flex w-full justify-between">
              <span className="font-bold">{t('orderDetail.createdAt')}</span>
              <span>
                {format(orderDetails?.createdAt ?? '', 'dd MMM yyyy')}
              </span>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
